/**
 * `Organization` and `LocalBusiness` structured data.
 *
 * This is the part of the SEO settings that does actual work. A meta keyword
 * containing "Karimnagar" does approximately nothing for a search engine;
 * `LocalBusiness` markup carrying a real street address, coordinates and
 * opening hours is what a shop appears in a map pack on. The fields exist to
 * feed this, so this is where the care goes.
 *
 * Two nodes, linked: the merchant becomes an `Organization`, each store a
 * `LocalBusiness` naming it as `parentOrganization`. That linkage is how a
 * search engine understands five shopfronts as one brand rather than five
 * unrelated businesses.
 */

export interface ResolvedBranding {
  storeName: string;
  legalName: string | null;
  businessType: string | null;
  additionalTypes: string | null;
  priceRange: string | null;
  googlePlaceId: string | null;
  whatsappNumber: string | null;
  serviceRadiusKm: number | null;
  specialHours: unknown;
  areaName: string | null;
  streetAddress: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string;
  latitude: number | null;
  longitude: number | null;
  serviceAreas: string | null;
  openingHours: unknown;
  contactPhone: string | null;
  contactEmail: string | null;
  mapsUrl: string | null;
  social: Record<string, string | null>;
}

/** schema.org types a merchant can sensibly pick, keyed by what they sell. */
export const BUSINESS_TYPES = [
  'Store', 'GroceryStore', 'ClothingStore', 'ElectronicsStore', 'HomeGoodsStore',
  'JewelryStore', 'ShoeStore', 'SportingGoodsStore', 'BookStore', 'FurnitureStore',
  'HealthAndBeautyBusiness', 'Restaurant', 'Bakery', 'Florist', 'PetStore',
] as const;

interface OpeningHour {
  day: string;
  opens?: string;
  closes?: string;
  closed?: boolean;
}

/**
 * The address, or null when it is not complete enough to publish.
 *
 * A partial address is worse than none: structured data is *believed*, so a
 * street with no locality can put a business on a map in the wrong town rather
 * than simply leaving it off.
 */
function postalAddress(b: ResolvedBranding): Record<string, unknown> | null {
  if (!b.streetAddress || !b.locality || !b.postalCode) return null;

  return {
    '@type': 'PostalAddress',
    streetAddress: b.streetAddress,
    addressLocality: b.locality,
    ...(b.region ? { addressRegion: b.region } : {}),
    postalCode: b.postalCode,
    addressCountry: b.countryCode,
  };
}

function openingHoursSpecification(hours: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(hours) || !hours.length) return null;

  const specs = (hours as OpeningHour[])
    // A closed day is expressed by leaving it out, not by a zero-length range.
    .filter((h) => h && h.day && !h.closed && h.opens && h.closes)
    .map((h) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${h.day}`,
      opens: h.opens,
      closes: h.closes,
    }));

  return specs.length ? specs : null;
}

interface SpecialHour {
  date: string;
  opens?: string;
  closes?: string;
  closed?: boolean;
}

/**
 * Festival and holiday overrides.
 *
 * A shop showing "Open now" on Diwali when it is shut has told a customer
 * something worse than nothing — they make a journey. A closed day is expressed
 * as a zero-length range at the date, which is what schema.org specifies for
 * "closed all day"; that is the one place a zero-length range is correct, and
 * the opposite of the rule for regular weekly hours.
 */
function specialOpeningHours(hours: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(hours) || !hours.length) return null;

  const specs = (hours as SpecialHour[])
    .filter((h) => h && h.date)
    .map((h) => ({
      '@type': 'OpeningHoursSpecification',
      validFrom: h.date,
      validThrough: h.date,
      opens: h.closed ? '00:00' : h.opens ?? '00:00',
      closes: h.closed ? '00:00' : h.closes ?? '00:00',
    }));

  return specs.length ? specs : null;
}

/** Social profile URLs, which schema.org expects as `sameAs`. */
function sameAs(social: Record<string, string | null>): string[] {
  const urls: string[] = [];
  for (const [network, value] of Object.entries(social)) {
    if (!value) continue;
    // Twitter is stored as a handle; the rest are already URLs.
    urls.push(
      network === 'twitter' && !value.startsWith('http')
        ? `https://x.com/${value.replace(/^@/, '')}`
        : value
    );
  }
  return urls;
}

/** The business. One node per merchant, referenced by each of their stores. */
export function organizationJsonLd(
  b: ResolvedBranding,
  logoUrl: string | null,
  baseUrl: string
): Record<string, unknown> {
  const profiles = sameAs(b.social);

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseUrl}#organization`,
    name: b.legalName ?? b.storeName,
    // The store's own address, not a free-text field beside it: StoreDomain is
    // what the merchant actually owns and what is verified.
    url: baseUrl,
    ...(logoUrl ? { logo: logoUrl } : {}),
    ...(profiles.length ? { sameAs: profiles } : {}),
    ...(b.contactPhone || b.contactEmail
      ? {
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            ...(b.contactPhone ? { telephone: b.contactPhone } : {}),
            ...(b.contactEmail ? { email: b.contactEmail } : {}),
          },
        }
      : {}),
  };
}

/** Click-to-chat, which is how a great deal of Indian local retail is contacted. */
export function whatsappLink(number: string | null): string | null {
  if (!number) return null;
  const digits = number.replace(/[^\d]/g, '');
  if (digits.length < 10) return null;
  // Assume the local country code when the merchant typed a bare number.
  const withCode = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCode}`;
}

/**
 * The shopfront.
 *
 * Returns null unless there is a publishable address — see {@link postalAddress}.
 * Emitting a `LocalBusiness` with no location is not a smaller win; it is a
 * claim to be a local business without saying where, which is not one.
 */
export function localBusinessJsonLd(
  b: ResolvedBranding,
  logoUrl: string | null,
  baseUrl: string
): Record<string, unknown> | null {
  const address = postalAddress(b);
  if (!address) return null;

  const hours = openingHoursSpecification(b.openingHours);
  const special = specialOpeningHours(b.specialHours);
  const areas = [b.areaName, ...(b.serviceAreas?.split(',') ?? [])]
    .map((a) => a?.trim())
    .filter((a): a is string => !!a);

  /*
   * A radius is a more honest statement of delivery reach than a list of town
   * names, and only meaningful with coordinates to centre it on — so it is
   * emitted alongside the named areas rather than instead of them.
   */
  const geoCircle =
    b.serviceRadiusKm && b.latitude !== null && b.longitude !== null
      ? {
          '@type': 'GeoCircle',
          geoMidpoint: { '@type': 'GeoCoordinates', latitude: b.latitude, longitude: b.longitude },
          geoRadius: b.serviceRadiusKm * 1000,
        }
      : null;

  // A shop that is both a bakery and a cafe says so with additionalType.
  const extraTypes = b.additionalTypes
    ?.split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => `https://schema.org/${t}`) ?? [];

  const sameAsUrls = [
    b.mapsUrl,
    whatsappLink(b.whatsappNumber),
  ].filter((u): u is string => !!u);

  return {
    '@context': 'https://schema.org',
    '@type': b.businessType ?? 'Store',
    '@id': `${baseUrl}#localbusiness`,
    name: b.storeName,
    url: baseUrl,
    ...(logoUrl ? { image: logoUrl } : {}),
    address,
    ...(b.latitude !== null && b.longitude !== null
      ? { geo: { '@type': 'GeoCoordinates', latitude: b.latitude, longitude: b.longitude } }
      : {}),
    ...(extraTypes.length ? { additionalType: extraTypes } : {}),
    // Rendered as ₹ / ₹₹ / ₹₹₹ directly in local results.
    ...(b.priceRange ? { priceRange: b.priceRange } : {}),
    ...(areas.length || geoCircle
      ? {
          areaServed: [
            ...areas.map((name) => ({ '@type': 'Place', name })),
            ...(geoCircle ? [geoCircle] : []),
          ],
        }
      : {}),
    ...(hours ? { openingHoursSpecification: hours } : {}),
    ...(special ? { specialOpeningHoursSpecification: special } : {}),
    ...(b.contactPhone ? { telephone: b.contactPhone } : {}),
    ...(b.contactEmail ? { email: b.contactEmail } : {}),
    ...(b.mapsUrl ? { hasMap: b.mapsUrl } : {}),
    // The Place ID is what actually ties this markup to a Google Business
    // Profile; a maps URL alone is just a link.
    ...(b.googlePlaceId
      ? {
          identifier: {
            '@type': 'PropertyValue',
            propertyID: 'googlePlaceId',
            value: b.googlePlaceId,
          },
        }
      : {}),
    ...(sameAsUrls.length ? { sameAs: sameAsUrls } : {}),
    parentOrganization: { '@id': `${baseUrl}#organization` },
  };
}

/**
 * A title for a local store page.
 *
 * "FreshMart — Groceries in Karimnagar" outperforms "FreshMart" for the search
 * people actually type, which includes the place name far more often than not.
 * Only used when the merchant has not written their own title.
 */
export function localTitle(storeName: string, areaName: string | null, businessType: string | null): string {
  if (!areaName) return storeName;

  const kind = businessType
    ? businessType.replace(/([a-z])([A-Z])/g, '$1 $2')
    : null;

  return kind ? `${storeName} — ${kind} in ${areaName}` : `${storeName} in ${areaName}`;
}

/**
 * A product page title, from the store's template.
 *
 * The point is reach: a merchant will write one good title for their homepage
 * and never touch the other four hundred pages. A template puts the town into
 * every one of them, which is the difference between ranking for "cake
 * Karimnagar" and ranking for nothing.
 *
 * Placeholders are deliberately few — {product}, {store}, {area} — because a
 * template language is a thing to learn, and this should be obvious at a glance.
 */
export function applyProductTitleTemplate(
  template: string | null,
  parts: { product: string; store: string; area: string | null }
): string {
  const fallback = `${parts.product} | ${parts.store}`;
  if (!template?.trim()) return fallback;

  const filled = template
    .replace(/\{product\}/gi, parts.product)
    .replace(/\{store\}/gi, parts.store)
    .replace(/\{area\}/gi, parts.area ?? '')
    // An unset area leaves "in  |" behind; tidy the seams rather than emitting
    // a title with a hole in it.
    .replace(/\s+(in|near)\s+(?=[|\-—]|$)/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*([|\-—])\s*$/,'')
    .trim();

  return filled || fallback;
}
